<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, array, date, string } from 'yup'
import {
  DCard,
  DBtn,
  DMultiselect,
  DTextField,
  DSelectFieldSearch,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon
} from 'front-library'
import {
  getLeaveReasons,
  getEmployees,
  getConcepts,
  createLiquidationRequest
} from '@/app/core/services/liquidation'
usePayrollChange(() => window.location.reload())

/* router, notifications */
const router = useRouter()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos Listas */
const itemsLeaveReasonConcepts = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const itemsExtraConcepts = ref<any[]>([])
const itemsLeaveReasonId = ref<any[]>([])
const searchLeaveReasonId = ref('')

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  leaveReasonId: string().required(
    t('form.valid.fieldRequired', {
      field: t('liquidations.valid.leaveReason')
    })
  ),
  leaveReasonConcepts: array()
    .min(1, t('form.valid.minArray'))
    .required(
      t('form.valid.fieldRequired', {
        field: t('fields.masterConcepts')
      })
    ),
  leaveDate: date().required(
    t('form.valid.fieldRequired', {
      field: t('fields.leaveDate')
    })
  ),
  employees: array()
    .min(1, t('liquidations.valid.employeeSelectionRequired'))
    .required(
      t('form.valid.fieldRequired', {
        field: t('fields.employees')
      })
    )
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<any>({
  initialValues: {},
  validationSchema
})

/* Definicion campos del formulario */
const { value: leaveReasonConcepts } = useField<string[]>(
  ref('leaveReasonConcepts')
)
const { value: leaveDate } = useField<any[]>(ref('leaveDate'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: extraConcepts } = useField<any[]>(ref('extraConcepts'))
const { value: leaveReasonId } = useField<string>(ref('leaveReasonId'))

/* Metodo que valida los campos obligatorios del formulario y genera la solicitud de la liquidacion */
const onSubmit = handleSubmit(async (values: any) => {
  loading.value = true
  messageLoading.value = ''
  let id: any = false
  try {
    id = await createLiquidationRequest(values)
    if (id) {
      loading.value = false
      notifications.addNotification({
        title: t('notification.created'),
        text: t('liquidations.notification.action', [
          t('notification.wasCreated')
        ]),
        type: 'success',
        time: 10000
      })
      void router.push({ name: 'GetLiquidationRequest', params: { id } })
    }
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
})

/* Listar conceptos y conceptos adicionales */
const listConcepts = async () => {
  leaveReasonConcepts.value = []
  itemsLeaveReasonConcepts.value = []
  extraConcepts.value = []
  itemsExtraConcepts.value = []
  loading.value = true
  messageLoading.value = t('loading.concepts')
  try {
    if (leaveReasonId.value !== '') {
      const masterConcepts = itemsLeaveReasonId.value.filter(
        (item: any) => item.id === leaveReasonId.value
      )[0].master_concepts
      const allConcepts = await getConcepts()
      itemsLeaveReasonConcepts.value = allConcepts.filter((item: any) =>
        masterConcepts.includes(item.master_concept_id)
      )
      leaveReasonConcepts.value = itemsLeaveReasonConcepts.value.map(
        (_: any) => _.id
      )
      itemsExtraConcepts.value = allConcepts.filter(
        (item: any) =>
          !masterConcepts.includes(item.id) &&
          !new Set(
            itemsLeaveReasonConcepts.value.map(item => item.master_concept_id)
          ).has(item.master_concept_id)
      )
    }
    loading.value = false
    if (itemsLeaveReasonConcepts.value.length === 0) {
      notifications.addNotification({
        title: t('liquidations.notification.emptyConcepts'),
        text: t('liquidations.notification.noConceptsFound'),
        type: 'warning',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'ListLiquidationRequests' })
}

onMounted(async () => {
  loading.value = true
  try {
    ;[itemsLeaveReasonId.value, itemsEmployees.value] = await Promise.all([
      getLeaveReasons(),
      getEmployees()
    ])
    employees.value = []
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('liquidations.create')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-6">
            <div class="col-span-6 lg:col-span-4 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3">
                  <DSelectFieldSearch
                    :label="t('liquidations.fields.leaveReason')"
                    v-model="leaveReasonId"
                    :options="
                      filterOptions(itemsLeaveReasonId, searchLeaveReasonId)
                    "
                    @filter="searchLeaveReasonId = $event"
                    @change="listConcepts"
                    :error="errors.leaveReasonId"
                    :required="true" />
                </div>
                <div class="col-span-6 sm:col-span-3">
                  <DTextField
                    type="date"
                    :label="t('liquidations.fields.leaveDate')"
                    v-model="leaveDate"
                    :error="errors.leaveDate"
                    :required="true" />
                </div>
                <div class="col-span-6 sm:col-span-3">
                  <DMultiselect
                    :label="t('liquidations.fields.concepts')"
                    v-model="leaveReasonConcepts"
                    :headerTitle="t('liquidations.fields.concepts')"
                    :required="true"
                    :options="itemsLeaveReasonConcepts"
                    :error="errors.leaveReasonConcepts" />
                </div>
                <div class="col-span-6 sm:col-span-3">
                  <DMultiselect
                    :label="t('liquidations.fields.additionalConcepts')"
                    v-model="extraConcepts"
                    :headerTitle="t('liquidations.fields.additionalConcepts')"
                    :options="itemsExtraConcepts" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-6 lg:col-span-2 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3 class="text-md font-medium leading-6 text-gray-900">
                  {{ t('liquidations.fields.employees') }}
                </h3>
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.create')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
